import { useEffect, useState, useCallback, useMemo } from "react";
import { api, getApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Calendar as CalIcon, Clock, Loader2, CheckCircle2, ChevronLeft, ChevronRight, CalendarDays,
} from "lucide-react";
import { Reveal } from "@/components/effects/Effects";

const CZ_DAYS = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
const CZ_MONTHS = ["led", "úno", "bře", "dub", "kvě", "čvn", "čvc", "srp", "zář", "říj", "lis", "pro"];
const WINDOW = 4;

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDaysISO(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toISO(d);
}
function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return `${CZ_DAYS[d.getDay()]} ${d.getDate()}. ${CZ_MONTHS[d.getMonth()]}`;
}

export default function BookingSection() {
  const [settings, setSettings] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", note: "", gift_voucher: false, num_players: 2, payment_type: "deposit", gdpr_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [payStatus, setPayStatus] = useState(null);
  const [windowStart, setWindowStart] = useState(toISO(new Date()));
  const [fullCalOpen, setFullCalOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherState, setVoucherState] = useState(null); // null | 'checking' | 'valid' | 'invalid'
  const [voucherMsg, setVoucherMsg] = useState("");

  const st = settings?.site_texts || {};
  const bt = (k, d) => (st[k] === undefined ? d : st[k]);

  const loadData = useCallback(async () => {
    try {
      const [s, sl] = await Promise.all([api.get("/settings/public"), api.get("/slots")]);
      setSettings(s.data);
      setSlots(sl.data);
    } catch (e) {
      toast.error("Nepodařilo se načíst termíny.");
    }
  }, []);

  const pollPayment = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPayStatus(null); toast.error("Platba se nepotvrdila včas. Zkontrolujte email."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPayStatus("success");
        toast.success("Platba proběhla úspěšně! Rezervace potvrzena.");
        loadData();
        window.history.replaceState({}, "", "/");
        return;
      }
      if (data.status === "expired") { setPayStatus(null); toast.error("Platba vypršela."); return; }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    } catch (e) { setPayStatus(null); }
  }, [loadData]);

  useEffect(() => {
    loadData();
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (sid) { setPayStatus("checking"); pollPayment(sid); }
    else if (cancelled) {
      api.post(`/reservations/${cancelled}/cancel`).finally(() => {
        window.history.replaceState({}, "", "/");
        loadData();
      });
      toast.info("Platba byla zrušena, termín je opět volný.");
    }
  }, [loadData, pollPayment]);

  const slotsByDate = useMemo(() => slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {}), [slots]);

  const availableDates = useMemo(
    () => slots.filter((s) => s.available).map((s) => new Date(s.date + "T00:00:00")), [slots]);

  const windowDays = useMemo(
    () => Array.from({ length: WINDOW }, (_, i) => addDaysISO(windowStart, i)), [windowStart]);

  const openBooking = (slot) => {
    setSelectedSlot(slot);
    setForm({ first_name: "", last_name: "", phone: "", email: "", note: "", gift_voucher: false, voucher_code: "", num_players: 2, payment_type: "deposit", gdpr_consent: false });
    setVoucherCode(""); setVoucherState(null); setVoucherMsg("");
  };

  const applyVoucher = async () => {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    setVoucherState("checking");
    try {
      const { data } = await api.get(`/vouchers/validate/${encodeURIComponent(code)}`);
      if (data.valid) {
        setVoucherState("valid"); setVoucherMsg(`Poukaz platný (${data.amount} Kč). Rezervace bude zdarma.`);
        setForm((f) => ({ ...f, gift_voucher: true, voucher_code: code }));
      } else {
        setVoucherState("invalid"); setVoucherMsg(data.reason || "Neplatný kód.");
        setForm((f) => ({ ...f, gift_voucher: false, voucher_code: "" }));
      }
    } catch (e) {
      setVoucherState("invalid"); setVoucherMsg("Ověření se nezdařilo.");
    }
  };

  const clearVoucher = () => {
    setVoucherCode(""); setVoucherState(null); setVoucherMsg("");
    setForm((f) => ({ ...f, gift_voucher: false, voucher_code: "" }));
  };

  const submitBooking = async () => {
    if (!form.first_name || !form.last_name || !form.phone || !form.email) {
      toast.error("Vyplňte prosím všechna povinná pole."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Zadejte platnou e-mailovou adresu."); return;
    }
    if (!form.gdpr_consent) { toast.error("Musíte souhlasit se zpracováním osobních údajů."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations", { slot_id: selectedSlot.id, ...form, origin_url: window.location.origin });
      if (data.requires_payment) window.location.href = data.checkout_url;
      else { setSelectedSlot(null); setPayStatus("success"); loadData(); }
    } catch (e) {
      toast.error(getApiError(e, bt("booking.error", "Rezervaci se nepodařilo dokončit.")));
    } finally { setSubmitting(false); }
  };

  const deposit = settings?.deposit_amount ?? 500;
  const perPerson = settings?.pricing_mode === "per_person";
  const ppp = settings?.price_per_person || {};
  const fullPrice = perPerson ? Number(ppp[String(form.num_players)] ?? settings?.full_price ?? 2500) : (settings?.full_price ?? 2500);
  const paymentRequired = settings?.payment_required ?? true;

  return (
    <section id="rezervace" className="relative bg-[#080706] border-b border-white/10" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-5 py-20 lg:py-28">
        <Reveal>
          <p className="text-xs tracking-[0.4em] text-nv uppercase mb-3 flex items-center gap-2">
            <CalIcon className="w-4 h-4" /> REZERVACE
          </p>
          <h2 className="font-head text-3xl sm:text-4xl text-neutral-100 mb-2 vhs-glitch" data-testid="booking-title">{bt("booking.title", "Vyber si termín")}</h2>
          <div className="mb-8">
            {bt("booking.subtitle", "Vyberte volný termín a dokončete rezervaci.") && <p className="text-sm text-neutral-500" data-testid="booking-subtitle">{bt("booking.subtitle", "Vyberte volný termín a dokončete rezervaci.")}</p>}
            {bt("booking.info", "") && <p className="text-sm text-neutral-400 mt-2 whitespace-pre-line" data-testid="booking-info">{bt("booking.info", "")}</p>}
          </div>
        </Reveal>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex gap-4 text-xs tracking-widest">
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-nv/70" /> VOLNO</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-800/60" /> OBSAZENO</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => setWindowStart(addDaysISO(windowStart, -WINDOW))}
              className="vhs-btn p-2 border border-white/10 bg-[#111] hover:border-nv text-neutral-300"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs text-neutral-400 tracking-widest min-w-[140px] text-center">
              {formatDate(windowDays[0])} – {formatDate(windowDays[WINDOW - 1])}
            </span>
            <button data-testid="cal-next" onClick={() => setWindowStart(addDaysISO(windowStart, WINDOW))}
              className="vhs-btn p-2 border border-white/10 bg-[#111] hover:border-nv text-neutral-300"><ChevronRight className="w-4 h-4" /></button>
            <button data-testid="open-full-calendar" onClick={() => setFullCalOpen(true)}
              className="vhs-btn px-3 py-2 border border-white/10 bg-[#111] hover:border-nv text-neutral-300 text-xs flex items-center gap-2">
              <CalendarDays className="w-4 h-4" /> Kalendář
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="calendar-grid">
          {windowDays.map((date) => {
            const daySlots = (slotsByDate[date] || []).slice().sort((a, b) => a.time.localeCompare(b.time));
            return (
              <div key={date} className="border border-white/10 bg-[#0b0a09] p-3 min-h-[120px]" data-testid={`day-${date}`}>
                <div className="font-head text-sm uppercase text-neutral-200 mb-3 border-b border-white/5 pb-2">{formatDate(date)}</div>
                <div className="flex flex-col gap-2">
                  {daySlots.length === 0 ? <span className="text-[11px] text-neutral-600">—</span>
                    : daySlots.map((slot) => (
                      <button key={slot.id} data-testid={`slot-${slot.id}`} disabled={!slot.available}
                        title={slot.held ? "Termín právě někdo rezervuje" : (slot.too_soon && !slot.is_booked ? "Příliš brzy na rezervaci" : "")}
                        onClick={() => openBooking(slot)}
                        className={`vhs-btn flex items-center gap-1 px-2 py-1.5 text-sm border font-mono justify-center ${
                          slot.available ? "border-nv/50 text-nv bg-nv/10 hover:bg-nv/20"
                          : slot.held ? "border-amber-700/50 text-amber-500/80 bg-amber-950/10 cursor-not-allowed animate-pulse"
                          : slot.too_soon && !slot.is_booked ? "border-white/10 text-neutral-600 bg-neutral-900/30 cursor-not-allowed"
                          : "border-red-900/40 text-red-700/70 bg-red-950/10 line-through cursor-not-allowed"}`}>
                        <Clock className="w-3 h-3" /> {slot.time}
                      </button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
        {slots.length === 0 && (
          <div className="border border-dashed border-white/10 p-12 text-center text-neutral-500 mt-4">
            {bt("booking.empty", "Momentálně nejsou vypsané žádné termíny. Zkuste to prosím později.")}
          </div>
        )}
      </div>

      {/* FULL CALENDAR */}
      <Dialog open={fullCalOpen} onOpenChange={setFullCalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-t-2 border-t-nv border-white/10 rounded-none max-w-fit text-neutral-200">
          <DialogHeader>
            <DialogTitle className="font-head uppercase tracking-wide">Vyber datum</DialogTitle>
            <DialogDescription className="text-neutral-500">Zeleně zvýrazněné dny mají volné termíny.</DialogDescription>
          </DialogHeader>
          <Calendar mode="single" data-testid="full-calendar" modifiers={{ available: availableDates }}
            modifiersClassNames={{ available: "text-nv font-bold border border-nv/50" }}
            onSelect={(d) => { if (d) { setWindowStart(toISO(d)); setFullCalOpen(false); } }}
            className="[&_.rdp]:text-neutral-200" />
        </DialogContent>
      </Dialog>

      {/* BOOKING DIALOG */}
      <Dialog open={!!selectedSlot} onOpenChange={(o) => !o && setSelectedSlot(null)}>
        <DialogContent className="bg-[#0a0a0a] border-t-2 border-t-nv border-white/10 rounded-none max-w-lg text-neutral-200 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head uppercase text-neutral-100 tracking-wide">Dokončit rezervaci</DialogTitle>
            <DialogDescription className="text-neutral-500">
              {selectedSlot && `Termín: ${formatDate(selectedSlot.date)} v ${selectedSlot.time}`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-neutral-400">Jméno *</Label>
              <Input data-testid="input-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
            <div><Label className="text-xs text-neutral-400">Příjmení *</Label>
              <Input data-testid="input-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-neutral-400">Telefon *</Label>
              <Input data-testid="input-phone" type="tel" autoComplete="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
            <div><Label className="text-xs text-neutral-400">Email *</Label>
              <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" /></div>
          </div>
          <div><Label className="text-xs text-neutral-400">Poznámka</Label>
            <Textarea data-testid="input-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="rounded-none bg-[#111] border-white/10 focus-visible:ring-nv mt-1" rows={2} /></div>
          <div><Label className="text-xs text-neutral-400">Počet hráčů</Label>
            <div className="flex gap-2 mt-1" data-testid="num-players">
              {[1, 2, 3, 4].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, num_players: n })} data-testid={`players-${n}`}
                  className={`flex-1 py-2 border text-sm font-head ${form.num_players === n ? "border-nv bg-nv/10 text-nv" : "border-white/10 text-neutral-400 hover:text-neutral-100"}`}>{n}</button>
              ))}
            </div>
            {perPerson && <p className="text-[11px] text-neutral-500 mt-1">Cena za {form.num_players} {form.num_players === 1 ? "hráče" : "hráče"}: <span className="text-nv">{fullPrice} Kč</span></p>}
          </div>
          {paymentRequired && !form.gift_voucher && (
            <div className="border border-white/10 p-3 bg-[#111]" data-testid="payment-options">
              <Label className="text-xs text-neutral-400 mb-2 block">Způsob platby</Label>
              <RadioGroup value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })} className="gap-2">
                <label className="flex items-center gap-3 cursor-pointer text-sm text-neutral-300">
                  <RadioGroupItem value="deposit" data-testid="pay-deposit" className="border-nv text-nv" />
                  Záloha <span className="text-nv font-bold">{deposit} Kč</span> (doplatek na místě)
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm text-neutral-300">
                  <RadioGroupItem value="full" data-testid="pay-full" className="border-nv text-nv" />
                  Celá částka <span className="text-nv font-bold">{fullPrice} Kč</span>
                </label>
              </RadioGroup>
            </div>
          )}
          <div className="border border-white/10 p-3 bg-[#111]" data-testid="voucher-redeem">
            <Label className="text-xs text-neutral-400 mb-2 block flex items-center gap-2">Máte dárkový poukaz?</Label>
            {voucherState === "valid" ? (
              <div className="flex items-center justify-between gap-2 text-sm text-nv" data-testid="voucher-applied">
                <span>✓ {voucherMsg}</span>
                <button onClick={clearVoucher} data-testid="voucher-clear-btn" className="text-xs text-neutral-500 hover:text-red-400 underline">odebrat</button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input data-testid="voucher-code-input" value={voucherCode} onChange={(e) => setVoucherCode(e.target.value)} placeholder="HUB-XXXX-XXXX"
                    className="rounded-none bg-black border-white/10 focus-visible:ring-nv font-mono uppercase" />
                  <button onClick={applyVoucher} disabled={voucherState === "checking"} data-testid="voucher-apply-btn"
                    className="hud-btn vhs-btn px-4 text-sm font-head shrink-0">
                    {voucherState === "checking" ? <Loader2 className="w-4 h-4 animate-spin" /> : "OVĚŘIT"}
                  </button>
                </div>
                {voucherState === "invalid" && <p className="text-xs text-red-400 mt-2" data-testid="voucher-error">{voucherMsg}</p>}
              </>
            )}
          </div>
          <div className="flex items-start gap-3 border border-white/10 p-3 bg-[#111]">
            <Checkbox data-testid="gdpr-checkbox" id="gdpr" checked={form.gdpr_consent}
              onCheckedChange={(v) => setForm({ ...form, gdpr_consent: !!v })}
              className="rounded-none border-nv data-[state=checked]:bg-nv data-[state=checked]:text-black mt-0.5" />
            <Label htmlFor="gdpr" className="text-xs text-neutral-400 cursor-pointer leading-relaxed">
              Souhlasím se zpracováním osobních údajů pro účely rezervace (GDPR). *
            </Label>
          </div>
          <div className="text-sm text-neutral-400">
            {form.gift_voucher ? "Rezervace bude dokončena bez platby (dárkový poukaz)."
              : !paymentRequired ? bt("booking.free_note", "Rezervace je zdarma, neplatí se žádná záloha.")
              : <>K platbě: <span className="text-nv font-bold">{form.payment_type === "full" ? fullPrice : deposit} Kč</span></>}
          </div>
          <button data-testid="submit-booking-btn" disabled={submitting} onClick={submitBooking}
            className="hud-btn vhs-btn font-head tracking-widest py-4 w-full flex items-center justify-center">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
              (form.gift_voucher || !paymentRequired) ? "DOKONČIT REZERVACI" :
              form.payment_type === "full" ? `ZAPLATIT ${fullPrice} KČ` : `ZAPLATIT ZÁLOHU ${deposit} KČ`}
          </button>
        </DialogContent>
      </Dialog>

      {/* PAYMENT STATUS */}
      <Dialog open={!!payStatus} onOpenChange={(o) => !o && payStatus === "success" && setPayStatus(null)}>
        <DialogContent className="bg-[#0a0a0a] border-t-2 border-t-nv border-white/10 rounded-none max-w-md text-center text-neutral-200">
          <DialogTitle className="sr-only">Stav platby</DialogTitle>
          <DialogDescription className="sr-only">Informace o stavu vaší rezervace a platby.</DialogDescription>
          {payStatus === "checking" ? (
            <div className="py-8">
              <Loader2 className="w-12 h-12 text-nv animate-spin mx-auto mb-4" />
              <p className="font-head uppercase tracking-widest">Ověřuji platbu…</p>
            </div>
          ) : (
            <div className="py-8" data-testid="success-message">
              <CheckCircle2 className="w-14 h-14 text-nv mx-auto mb-4 glow-nv" />
              <h3 className="font-head text-2xl uppercase text-neutral-100 mb-2" data-testid="success-title">{bt("booking.success_title", "Rezervace potvrzena")}</h3>
              <p className="text-sm text-neutral-400" data-testid="success-body">{bt("booking.success_body", "Potvrzení jsme vám poslali na email. Těšíme se na vás!")}</p>
              <button onClick={() => setPayStatus(null)} className="hud-btn vhs-btn mt-6 px-6 py-2 font-head">ZAVŘÍT</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
